import {
  addDoc,
  arrayUnion,
  collection,
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  Unsubscribe,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { UserNotification } from "../@types/user";
import { uploadReportFiles } from "./storage";
import {
  NewSupportTicketInput,
  SupportActor,
  SupportAgentProposal,
  SupportTicket,
  SupportTicketEvent,
  SupportTicketStatus,
} from "../@types/support";

const SUBJECT_MAX_LENGTH = 80;

// Days after resolution during which the user can reopen instead of opening a
// new ticket.
export const SUPPORT_REOPEN_WINDOW_DAYS = 7;

export const supportTicketsCollectionRef = (userId: string) =>
  collection(db, `users/${userId}/supportTickets`);

export const supportTicketDocRef = (userId: string, ticketId: string) =>
  doc(db, `users/${userId}/supportTickets/${ticketId}`);

export const supportTicketEventsCollectionRef = (
  userId: string,
  ticketId: string
) => collection(db, `users/${userId}/supportTickets/${ticketId}/events`);

const supportMetadataDocRef = (userId: string) =>
  doc(db, `users/${userId}/metadata/support`);

// ---------------------------------------------------------------------------
// Bell notifications (users/{uid}/metadata/notifications). One notification
// per ticket, replaced on each team action, marked read when the ticket is
// opened. All best-effort: never let the bell break the ticket flow.
// ---------------------------------------------------------------------------

const notificationsDocRef = (userId: string) =>
  doc(db, `users/${userId}/metadata/notifications`);

const supportNotificationId = (ticketId: string) => `support-${ticketId}`;

export const upsertSupportNotification = async (
  ticket: SupportTicket,
  title: string
): Promise<void> => {
  try {
    const ref = notificationsDocRef(ticket.userId);
    const snap = await getDoc(ref);
    const all: UserNotification[] = snap.exists()
      ? (snap.data() as { all?: UserNotification[] }).all ?? []
      : [];
    const id = supportNotificationId(ticket.uid);
    const next = all.filter((n) => n.id !== id);
    next.push({
      id,
      title,
      body: ticket.subject,
      link: `/settings?support=${ticket.uid}`,
      linkText: "View ticket",
      isAdmin: false,
      read: false,
      readAt: null,
      createdAt: Timestamp.now(),
    });
    await setDoc(ref, { all: next }, { merge: true });
  } catch (error) {
    console.error("Support notification upsert failed", error);
  }
};

export const markSupportNotificationRead = async (
  userId: string,
  ticketId: string
): Promise<void> => {
  try {
    const ref = notificationsDocRef(userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const all: UserNotification[] =
      (snap.data() as { all?: UserNotification[] }).all ?? [];
    const id = supportNotificationId(ticketId);
    let changed = false;
    const next = all.map((n) => {
      if (n.id === id && !n.read) {
        changed = true;
        return { ...n, read: true, readAt: Timestamp.now() };
      }
      return n;
    });
    if (changed) await updateDoc(ref, { all: next });
  } catch (error) {
    console.error("Support notification read-mark failed", error);
  }
};

export const buildSupportSubject = (message: string) => {
  const firstLine = message.trim().split(/\r?\n/)[0] ?? "";
  const collapsed = firstLine.replace(/\s+/g, " ").trim();
  if (collapsed.length <= SUBJECT_MAX_LENGTH) return collapsed;
  return `${collapsed.slice(0, SUBJECT_MAX_LENGTH - 1).trimEnd()}…`;
};

interface CreateSupportTicketArgs {
  userId: string;
  userName: string;
  userEmail: string;
  createdBy: SupportActor;
  input: NewSupportTicketInput;
}

export const createSupportTicket = async ({
  userId,
  userName,
  userEmail,
  createdBy,
  input,
}: CreateSupportTicketArgs): Promise<SupportTicket> => {
  const createdAt = Timestamp.now();

  // Upload outside the transaction: storage is not transactional and retries
  // would re-upload.
  const files =
    input.files.length > 0
      ? await uploadReportFiles(input.files, userId, createdAt.toMillis())
      : [];

  const ticketRef = doc(supportTicketsCollectionRef(userId));
  const metadataRef = supportMetadataDocRef(userId);

  const ticket = await runTransaction(db, async (transaction) => {
    const metadataSnap = await transaction.get(metadataRef);
    const currentCounter: number = metadataSnap.data()?.ticketNum || 0;
    const ticketNumber = currentCounter + 1;

    const newTicket: SupportTicket = {
      uid: ticketRef.id,
      ticketNumber,
      category: input.category,
      status: "open",
      subject: buildSupportSubject(input.message),
      message: input.message,
      affectedAreas: input.category === "issue" ? input.affectedAreas : [],
      relatedJobs: input.category === "issue" ? input.relatedJobs : [],
      files,
      userId,
      userName,
      userEmail,
      createdBy,
      context: input.context,
      createdAt,
      updatedAt: createdAt,
      lastReplyAt: null,
      lastReplyBy: null,
      unreadForUser: false,
      unreadForTeam: true,
      resolvedAt: null,
    };

    transaction.set(ticketRef, newTicket);
    transaction.set(metadataRef, { ticketNum: ticketNumber }, { merge: true });

    return newTicket;
  });

  // Transitional dual-write so the legacy report inbox keeps working until the
  // admin view ships. Best-effort; never fail ticket creation on it.
  try {
    const legacyType = input.category === "issue" ? "report" : "suggestion";
    await addDoc(collection(db, `general/messages/${legacyType}`), {
      type: input.category === "issue" ? "Report" : "Suggestion",
      message: input.message,
      userId,
      userName,
      createdAt,
      done: false,
      affectedAreas: ticket.affectedAreas,
      files,
      supportTicketId: ticket.uid,
      supportTicketNumber: ticket.ticketNumber,
    });
  } catch (error) {
    console.error("Legacy report dual-write failed", error);
  }

  return ticket;
};

export const subscribeToSupportTickets = (
  userId: string,
  callback: (tickets: SupportTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const q = query(
    supportTicketsCollectionRef(userId),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (docSnap) =>
            ({ ...docSnap.data(), uid: docSnap.id } as SupportTicket)
        )
      );
    },
    (error) => onError?.(error)
  );
};

export const subscribeToSupportTicketEvents = (
  userId: string,
  ticketId: string,
  callback: (events: SupportTicketEvent[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const q = query(
    supportTicketEventsCollectionRef(userId, ticketId),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (docSnap) =>
            ({ ...docSnap.data(), uid: docSnap.id } as SupportTicketEvent)
        )
      );
    },
    (error) => onError?.(error)
  );
};

interface AddSupportReplyArgs {
  userId: string;
  ticket: SupportTicket;
  author: SupportActor;
  body: string;
  files: File[];
}

export const addSupportUserReply = async ({
  userId,
  ticket,
  author,
  body,
  files,
}: AddSupportReplyArgs): Promise<void> => {
  const createdAt = Timestamp.now();

  const uploaded =
    files.length > 0
      ? await uploadReportFiles(files, userId, createdAt.toMillis())
      : [];

  const event: Omit<SupportTicketEvent, "uid"> = {
    type: "reply",
    author: "user",
    authorName: author.fullName,
    body,
    files: uploaded,
    createdAt,
  };

  await addDoc(supportTicketEventsCollectionRef(userId, ticket.uid), event);

  // A user reply on "waiting on you" hands the ball back to us.
  const nextStatus: SupportTicketStatus =
    ticket.status === "waiting_on_you" ? "open" : ticket.status;

  await updateDoc(supportTicketDocRef(userId, ticket.uid), {
    updatedAt: createdAt,
    lastReplyAt: createdAt,
    lastReplyBy: "user",
    unreadForTeam: true,
    status: nextStatus,
  });
};

export const markSupportTicketReadByUser = async (
  userId: string,
  ticketId: string
): Promise<void> => {
  await updateDoc(supportTicketDocRef(userId, ticketId), {
    unreadForUser: false,
  });
  await markSupportNotificationRead(userId, ticketId);
};

export const canReopenSupportTicket = (ticket: SupportTicket): boolean => {
  if (ticket.status !== "resolved" || !ticket.resolvedAt) return false;
  const ageMs = Date.now() - ticket.resolvedAt.toMillis();
  return ageMs <= SUPPORT_REOPEN_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

export const reopenSupportTicket = async (
  userId: string,
  ticket: SupportTicket,
  author: SupportActor
): Promise<void> => {
  if (!canReopenSupportTicket(ticket)) {
    throw new Error(
      `This ticket was resolved more than ${SUPPORT_REOPEN_WINDOW_DAYS} days ago. Please open a new ticket.`
    );
  }

  const createdAt = Timestamp.now();

  const event: Omit<SupportTicketEvent, "uid"> = {
    type: "status_change",
    author: "user",
    authorName: author.fullName,
    fromStatus: "resolved",
    toStatus: "open",
    createdAt,
  };

  await addDoc(supportTicketEventsCollectionRef(userId, ticket.uid), event);

  await updateDoc(supportTicketDocRef(userId, ticket.uid), {
    status: "open",
    resolvedAt: null,
    updatedAt: createdAt,
    unreadForTeam: true,
  });
};

// ---------------------------------------------------------------------------
// Admin side (MountMonitor staff). Client-side writes are permitted for the
// admin UIDs by Firestore rules; everyone else is rejected server-side.
// ---------------------------------------------------------------------------

export const subscribeToAllSupportTickets = (
  callback: (tickets: SupportTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const q = query(
    collectionGroup(db, "supportTickets"),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (docSnap) => ({ ...docSnap.data(), uid: docSnap.id } as SupportTicket)
        )
      );
    },
    (error) => onError?.(error)
  );
};

interface AddSupportTeamReplyArgs {
  ticket: SupportTicket;
  authorName: string;
  body: string;
  files?: File[];
  // Optionally move the ticket in the same action (e.g. reply + waiting_on_you).
  setStatus?: SupportTicketStatus;
}

export const addSupportTeamReply = async ({
  ticket,
  authorName,
  body,
  files = [],
  setStatus,
}: AddSupportTeamReplyArgs): Promise<void> => {
  const createdAt = Timestamp.now();

  // Stored under the tenant's path so the URLs live with their ticket.
  const uploaded =
    files.length > 0
      ? await uploadReportFiles(files, ticket.userId, createdAt.toMillis())
      : [];

  const event: Omit<SupportTicketEvent, "uid"> = {
    type: "reply",
    author: "team",
    authorName,
    body,
    files: uploaded,
    createdAt,
  };

  await addDoc(
    supportTicketEventsCollectionRef(ticket.userId, ticket.uid),
    event
  );

  const nextStatus = setStatus ?? ticket.status;

  if (setStatus && setStatus !== ticket.status) {
    const statusEvent: Omit<SupportTicketEvent, "uid"> = {
      type: "status_change",
      author: "team",
      authorName,
      fromStatus: ticket.status,
      toStatus: setStatus,
      createdAt,
    };
    await addDoc(
      supportTicketEventsCollectionRef(ticket.userId, ticket.uid),
      statusEvent
    );
  }

  await updateDoc(supportTicketDocRef(ticket.userId, ticket.uid), {
    updatedAt: createdAt,
    lastReplyAt: createdAt,
    lastReplyBy: "team",
    unreadForUser: true,
    unreadForTeam: false,
    status: nextStatus,
    resolvedAt: nextStatus === "resolved" ? createdAt : null,
  });

  await upsertSupportNotification(
    ticket,
    `Support replied to your ticket #${ticket.ticketNumber}`
  );
};

export const setSupportTicketStatus = async (
  ticket: SupportTicket,
  toStatus: SupportTicketStatus,
  authorName: string
): Promise<void> => {
  if (toStatus === ticket.status) return;
  const createdAt = Timestamp.now();

  const event: Omit<SupportTicketEvent, "uid"> = {
    type: "status_change",
    author: "team",
    authorName,
    fromStatus: ticket.status,
    toStatus,
    createdAt,
  };

  await addDoc(
    supportTicketEventsCollectionRef(ticket.userId, ticket.uid),
    event
  );

  await updateDoc(supportTicketDocRef(ticket.userId, ticket.uid), {
    status: toStatus,
    updatedAt: createdAt,
    unreadForUser: true,
    resolvedAt: toStatus === "resolved" ? createdAt : null,
  });

  if (toStatus === "waiting_on_you" || toStatus === "resolved") {
    await upsertSupportNotification(
      ticket,
      toStatus === "resolved"
        ? `Your support ticket #${ticket.ticketNumber} has been resolved`
        : `Your support ticket #${ticket.ticketNumber} needs more information`
    );
  }
};

export const markSupportTicketReadByTeam = async (
  ticket: SupportTicket
): Promise<void> => {
  await updateDoc(supportTicketDocRef(ticket.userId, ticket.uid), {
    unreadForTeam: false,
  });
};

// ---------------------------------------------------------------------------
// Agent proposals (phase 4). The Hetzner daemon writes these via the Admin
// SDK; the admin tab reads/decides them client-side under the admin-uid rule.
// ---------------------------------------------------------------------------

export const supportProposalDocRef = (tenantUid: string, ticketUid: string) =>
  doc(db, `supportProposals/${tenantUid}_${ticketUid}`);

export const subscribeToSupportProposal = (
  tenantUid: string,
  ticketUid: string,
  callback: (proposal: SupportAgentProposal | null) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  return onSnapshot(
    supportProposalDocRef(tenantUid, ticketUid),
    (snap) => {
      callback(snap.exists() ? (snap.data() as SupportAgentProposal) : null);
    },
    (error) => onError?.(error)
  );
};

export const sendAgentInstruction = async (
  proposal: SupportAgentProposal,
  text: string
): Promise<void> => {
  await updateDoc(
    supportProposalDocRef(proposal.tenantUid, proposal.ticketUid),
    {
      dialogue: arrayUnion({ role: "admin", at: Date.now(), text }),
      instructionPending: true,
      status: "pending",
    }
  );
};

export const decideSupportProposal = async (
  proposal: SupportAgentProposal,
  status: "approved" | "rejected",
  decidedBy: string
): Promise<void> => {
  await updateDoc(
    supportProposalDocRef(proposal.tenantUid, proposal.ticketUid),
    {
      status,
      decidedAt: Timestamp.now(),
      decidedBy,
    }
  );
};
